import { ArrowLeft } from "lucide-react";
import { ReactNode, useMemo, useState } from "react";

import { SelectableOptionList } from "./SelectableOptionList";

import { getItemColor } from "@/components/UnifiedRegistry/utils";
import { Button } from "@/components/ui/button";
import { DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EntityIcon } from "@/components/ui/icons";
import SearchBar from "@/components/ui/SearchBar";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { cn } from "@/lib/utils";
import { Entity, EntityType, SmartSheet, useListSheets } from "@/queries";

interface SmartTableSheetViewProps {
  table: Entity<EntityType.SMART_TABLE>;
  readonly?: boolean;
  onSelect: (sheet: SmartSheet) => void;
  onOpenChange: (open: boolean) => void;
  onBack: () => void;
}

const TableTrail = ({ label }: { label: string }) => (
  <div className="flex items-center gap-1 text-xs text-gray-500">
    <OverflowDisplayTooltip content={label} className="min-w-0">
      <span className="block truncate">{label}</span>
    </OverflowDisplayTooltip>
  </div>
);

const SearchHeader = ({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) => (
  <div className="border-b border-gray-100 px-6 py-3">
    <SearchBar
      value={value}
      onChange={onChange}
      placeholderPhrasesArray={[placeholder]}
      size="xs"
      className="w-full"
      inputClassName="bg-transparent"
    />
  </div>
);

const getSheetLeading = () => <EntityIcon type={EntityType.SMART_TABLE} size="sm" />;

const getSheetLabel = (sheet: SmartSheet) => (
  <OverflowDisplayTooltip content={sheet.title} className="min-w-0 flex-1">
    <span className="block truncate font-medium text-gray-900">{sheet.title}</span>
  </OverflowDisplayTooltip>
);

const getSheetDescription = (sheet: SmartSheet) => {
  const columnCount = Array.isArray(sheet.columns) ? sheet.columns.length : undefined;
  return columnCount === undefined ? undefined : (
    <span className="text-xs text-gray-500">
      {columnCount} {columnCount === 1 ? "column" : "columns"}
    </span>
  );
};

const SheetList = ({
  isLoading,
  sheets,
  selectedSheet,
  readonly,
  onSelect,
}: {
  isLoading: boolean;
  sheets: SmartSheet[];
  selectedSheet: SmartSheet | null;
  readonly: boolean;
  onSelect: (sheet: SmartSheet) => void;
}) => {
  const [query, setQuery] = useState("");
  const showSearch = sheets.length > 5;

  const filteredSheets = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return sheets;
    }
    return sheets.filter(sheet => sheet.title.toLowerCase().includes(q));
  }, [query, sheets]);

  const hasNoSheets = sheets.length === 0;

  return (
    <div className="flex h-full flex-col">
      {showSearch && <SearchHeader value={query} onChange={setQuery} placeholder="Search sheets" />}

      <SelectableOptionList
        isLoading={isLoading}
        loadingMessage="Loading sheets..."
        emptyStateTitle={hasNoSheets ? "No sheets available" : "No matching sheets"}
        emptyStateDescription={
          hasNoSheets
            ? "This smart table does not have any sheets to evaluate yet."
            : "Try a different search."
        }
        items={filteredSheets}
        selectedKey={selectedSheet?.id ?? null}
        readonly={readonly}
        getItemKey={sheet => sheet.id}
        getItemLeading={getSheetLeading}
        getItemLabel={getSheetLabel}
        getItemDescription={getSheetDescription}
        onItemSelect={onSelect}
      />
    </div>
  );
};

const Footer = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
    {children}
  </div>
);

const SmartTableSheetView = ({
  table,
  readonly = false,
  onSelect,
  onOpenChange,
  onBack,
}: SmartTableSheetViewProps) => {
  const [selectedSheet, setSelectedSheet] = useState<SmartSheet | null>(null);
  const sheetsQuery = useListSheets(String(table.id), { limit: 100 });

  const sortedSheets = useMemo(
    () => [...(sheetsQuery.data?.items ?? [])].sort((a, b) => a.index - b.index),
    [sheetsQuery.data?.items],
  );

  const handleConfirm = () => {
    if (!selectedSheet) {
      return;
    }

    onSelect(selectedSheet);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <DialogHeader className="space-y-2 border-b border-gray-200 px-6 py-4">
        <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-gray-900">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            aria-label="Back"
            className="h-10 w-10 flex-shrink-0 p-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div
            className={cn(
              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full",
              getItemColor(EntityType.SMART_TABLE),
            )}
          >
            <EntityIcon type={EntityType.SMART_TABLE} size="md" />
          </div>
          <span className="min-w-0 flex-1 truncate">Which sheet?</span>
        </DialogTitle>
        <DialogDescription className="pl-[52px] text-sm text-gray-600">
          Pick the sheet you want to evaluate.
        </DialogDescription>
        <div className="pl-[52px]">
          <TableTrail label={table.name} />
        </div>
      </DialogHeader>

      <div className="min-h-0 flex-1 overflow-hidden">
        <SheetList
          isLoading={sheetsQuery.isLoading}
          sheets={sortedSheets}
          selectedSheet={selectedSheet}
          readonly={readonly}
          onSelect={setSelectedSheet}
        />
      </div>

      <Footer>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={readonly || !selectedSheet}>
          Confirm
        </Button>
      </Footer>
    </div>
  );
};

export default SmartTableSheetView;
